/**
 * Badge primitive — design.md § Components: Credential badge.
 *
 * Uses rounded-pill (999px) per design.md § Shapes.
 * Variants: default (accent-l bg, accent border, accent text),
 *   neutral (bg-warm bg, line-2 border, muted text),
 *   success, warning, error.
 * Uses font-mono per design.md eyebrow spec.
 */
import type { HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

export type BadgeVariant = 'default' | 'neutral' | 'success' | 'warning' | 'error';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  children: ReactNode;
}

const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-accent-l border-accent text-accent',
  neutral: 'bg-bg-warm border-line-2 text-muted',
  success: 'bg-success/10 border-success/30 text-success',
  warning: 'bg-warning/10 border-warning/30 text-warning',
  error: 'bg-error/10 border-error/30 text-error',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  function Badge(
    {
      variant = 'default',
      className,
      children,
      ...props
    },
    ref,
  ) {
    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center gap-1.5',
          'rounded-pill border',
          'px-2.5 py-0.5',
          'font-mono text-eyebrow font-medium uppercase',
          variantClasses[variant],
          className,
        )}
        {...props}
      >
        {children}
      </span>
    );
  },
);
